import React from 'react';
import './Chart.css';

interface ChartDataItem {
  label: string;
  value: number;
  negativo?: number;
  positivo?: number;
  neutro?: number;
}

interface ChartProps {
  type: 'bar' | 'pie' | 'line';
  data: ChartDataItem[];
}

const Chart: React.FC<ChartProps> = ({ type, data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="chart-empty">
        <p>Nenhum dado disponível</p>
      </div>
    );
  }

  const maxValue = Math.max(...data.map(item => item.value));

  const renderBarChart = () => {
    return (
      <div className="bar-chart">
        {data.map((item, index) => (
          <div key={index} className="bar-item">
            <div className="bar-container">
              <div
                className="bar"
                style={{
                  height: `${(item.value / maxValue) * 100}%`,
                  backgroundColor: `hsl(${(index * 60) % 360}, 70%, 50%)`
                }}
              >
                <span className="bar-value">{item.value}</span>
              </div>
            </div>
            <div className="bar-label">{item.label}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderPieChart = () => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    let cumulativePercentage = 0;

    return (
      <div className="pie-chart">
        <svg width="200" height="200" viewBox="0 0 200 200">
          {data.map((item, index) => {
            const percentage = (item.value / total) * 100;
            const startAngle = (cumulativePercentage / 100) * 360;
            const endAngle = ((cumulativePercentage + percentage) / 100) * 360;
            
            const x1 = 100 + 80 * Math.cos((startAngle - 90) * Math.PI / 180);
            const y1 = 100 + 80 * Math.sin((startAngle - 90) * Math.PI / 180);
            const x2 = 100 + 80 * Math.cos((endAngle - 90) * Math.PI / 180);
            const y2 = 100 + 80 * Math.sin((endAngle - 90) * Math.PI / 180);
            
            const largeArcFlag = percentage > 50 ? 1 : 0;
            
            const pathData = [
              `M 100 100`,
              `L ${x1} ${y1}`,
              `A 80 80 0 ${largeArcFlag} 1 ${x2} ${y2}`,
              `Z`
            ].join(' ');
            
            cumulativePercentage += percentage;
            
            return (
              <path
                key={index}
                d={pathData}
                fill={`hsl(${(index * 60) % 360}, 70%, 50%)`}
                stroke="white"
                strokeWidth="2"
              />
            );
          })}
        </svg>
        <div className="pie-legend">
          {data.map((item, index) => (
            <div key={index} className="legend-item">
              <div
                className="legend-color"
                style={{ backgroundColor: `hsl(${(index * 60) % 360}, 70%, 50%)` }}
              />
              <span className="legend-label">{item.label}</span>
              <span className="legend-value">({item.value})</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderLineChart = () => {
    const maxValue = Math.max(...data.map(item => item.value));
    const width = 400;
    const height = 200;
    const padding = 40;
    
    const points = data.map((item, index) => {
      const x = padding + (index * (width - 2 * padding)) / (data.length - 1);
      const y = height - padding - ((item.value / maxValue) * (height - 2 * padding));
      return { x, y, value: item.value };
    });

    const pathData = points.map((point, index) => 
      `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
    ).join(' ');

    return (
      <div className="line-chart">
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
          {/* Grid lines */}
          {[0, 1, 2, 3, 4].map(i => (
            <line
              key={i}
              x1={padding}
              y1={padding + i * (height - 2 * padding) / 4}
              x2={width - padding}
              y2={padding + i * (height - 2 * padding) / 4}
              stroke="#e5e7eb"
              strokeWidth="1"
            />
          ))}
          
          {/* Line */}
          <path
            d={pathData}
            fill="none"
            stroke="#667eea"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          
          {/* Points */}
          {points.map((point, index) => (
            <circle
              key={index}
              cx={point.x}
              cy={point.y}
              r="4"
              fill="#667eea"
              stroke="white"
              strokeWidth="2"
            />
          ))}
        </svg>
        <div className="line-labels">
          {data.map((item, index) => (
            <div key={index} className="line-label">
              {item.label}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="chart-container">
      {type === 'bar' && renderBarChart()}
      {type === 'pie' && renderPieChart()}
      {type === 'line' && renderLineChart()}
    </div>
  );
};

export default Chart; 